interface PointerCoordinates {
  x: number;
  y: number;
}

interface SelectionPoint {
  node: Node;
  offset: number;
}

function isNodeWithinElement(node: Node | null, element: HTMLElement): boolean {
  return node !== null && (node === element || element.contains(node));
}

function getTextSelectionPoint(element: HTMLElement, node: Node | null, offset: number): SelectionPoint | undefined {
  if (node?.nodeType !== 3 || !element.contains(node) || offset < 0 || offset > (node.textContent?.length ?? 0)) {
    return undefined;
  }

  return { node, offset };
}

function getTextSelectionPointAtCoordinates(
  element: HTMLElement,
  { x, y }: PointerCoordinates,
): SelectionPoint | undefined {
  const ownerDocument = element.ownerDocument;

  try {
    if (typeof ownerDocument.caretPositionFromPoint === "function") {
      const caretPosition = ownerDocument.caretPositionFromPoint(x, y);
      const selectionPoint = caretPosition
        ? getTextSelectionPoint(element, caretPosition.offsetNode, caretPosition.offset)
        : undefined;

      if (selectionPoint) {
        return selectionPoint;
      }
    }

    if (typeof ownerDocument.caretRangeFromPoint === "function") {
      const caretRange = ownerDocument.caretRangeFromPoint(x, y);
      const selectionPoint = caretRange
        ? getTextSelectionPoint(element, caretRange.startContainer, caretRange.startOffset)
        : undefined;

      if (selectionPoint) {
        return selectionPoint;
      }
    }
  } catch {
    return undefined;
  }

  return undefined;
}

function getTextOffset(element: HTMLElement, point: SelectionPoint): number | undefined {
  const range = element.ownerDocument.createRange();

  try {
    range.setStart(element, 0);
    range.setEnd(point.node, point.offset);
    return range.toString().length;
  } catch {
    return undefined;
  }
}

function isPointWithinElement(element: HTMLElement, { x, y }: PointerCoordinates): boolean {
  const rect = element.getBoundingClientRect();

  return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
}

function getTextBoundarySelectionPoint(element: HTMLElement, atEnd: boolean): SelectionPoint | undefined {
  const nodeFilter = element.ownerDocument.defaultView?.NodeFilter;

  if (!nodeFilter) {
    return undefined;
  }

  const walker = element.ownerDocument.createTreeWalker(element, nodeFilter.SHOW_TEXT);
  let textNode = walker.nextNode();

  if (!textNode) {
    return undefined;
  }

  if (atEnd) {
    for (let nextNode = walker.nextNode(); nextNode; nextNode = walker.nextNode()) {
      textNode = nextNode;
    }
  }

  return { node: textNode, offset: atEnd ? (textNode.textContent?.length ?? 0) : 0 };
}

function getSelectionPointOutsideElement(
  element: HTMLElement,
  { x, y }: PointerCoordinates,
): SelectionPoint | undefined {
  const rect = element.getBoundingClientRect();
  const isAfterElement = y > rect.bottom || (y >= rect.top && x > rect.right);

  return getTextBoundarySelectionPoint(element, isAfterElement);
}

function getClosestSelectionBoundary(element: HTMLElement, node: Node, offset: number): number | undefined {
  const ownerDocument = element.ownerDocument;
  const rangeConstructor = ownerDocument.defaultView?.Range;

  if (!rangeConstructor || (node !== ownerDocument && node.ownerDocument !== ownerDocument)) {
    return undefined;
  }

  const selectionPoint = ownerDocument.createRange();
  const elementContents = ownerDocument.createRange();

  try {
    selectionPoint.setStart(node, offset);
    selectionPoint.collapse(true);
    elementContents.selectNodeContents(element);

    return selectionPoint.compareBoundaryPoints(rangeConstructor.START_TO_START, elementContents) <= 0
      ? 0
      : element.childNodes.length;
  } catch {
    return undefined;
  }
}

function constrainSelectionToElement(
  selection: Selection,
  element: HTMLElement,
  selectionAnchor?: SelectionPoint,
  selectionFocus?: SelectionPoint,
): void {
  const { anchorNode, focusNode } = selection;

  if (!anchorNode || !focusNode) {
    return;
  }

  const isAnchorWithinElement = isNodeWithinElement(anchorNode, element);
  const isFocusWithinElement = isNodeWithinElement(focusNode, element);

  if (!selectionAnchor && !selectionFocus && isAnchorWithinElement === isFocusWithinElement) {
    return;
  }

  const anchorOffset = selectionAnchor
    ? selectionAnchor.offset
    : isAnchorWithinElement
      ? selection.anchorOffset
      : getClosestSelectionBoundary(element, anchorNode, selection.anchorOffset);
  const focusOffset = selectionFocus
    ? selectionFocus.offset
    : isFocusWithinElement
      ? selection.focusOffset
      : getClosestSelectionBoundary(element, focusNode, selection.focusOffset);

  if (anchorOffset === undefined || focusOffset === undefined) {
    return;
  }

  const nextAnchorNode = selectionAnchor?.node ?? (isAnchorWithinElement ? anchorNode : element);
  const nextFocusNode = selectionFocus?.node ?? (isFocusWithinElement ? focusNode : element);

  if (
    anchorNode === nextAnchorNode &&
    selection.anchorOffset === anchorOffset &&
    focusNode === nextFocusNode &&
    selection.focusOffset === focusOffset
  ) {
    return;
  }

  selection.setBaseAndExtent(nextAnchorNode, anchorOffset, nextFocusNode, focusOffset);
}

export function bindDatSnapshotTextSelection(snapshotContent: HTMLElement): () => void {
  const ownerDocument = snapshotContent.ownerDocument;
  const ownerWindow = ownerDocument.defaultView;

  if (!ownerWindow) {
    return () => {};
  }

  let isMouseSelecting = false;
  let isCustomMouseSelecting = false;
  let isPointerWithinSnapshot = false;
  let mousePointerCoordinates: PointerCoordinates | undefined;
  let mouseDownSelectionPoint: SelectionPoint | undefined;
  let mouseSelectionAnchor: SelectionPoint | undefined;
  let mouseSelectionFocus: SelectionPoint | undefined;

  const resetMouseSelection = (): void => {
    isMouseSelecting = false;
    isCustomMouseSelecting = false;
    isPointerWithinSnapshot = false;
    mousePointerCoordinates = undefined;
    mouseDownSelectionPoint = undefined;
    mouseSelectionAnchor = undefined;
    mouseSelectionFocus = undefined;
  };

  const handleSelectionChange = (): void => {
    const selection = ownerDocument.getSelection();

    if (!selection) {
      return;
    }

    let constrainedFocus: SelectionPoint | undefined;

    if (isMouseSelecting && isCustomMouseSelecting) {
      constrainedFocus = mouseSelectionFocus;
    } else if (isMouseSelecting) {
      if (!mouseSelectionAnchor) {
        const nativeAnchor = getTextSelectionPoint(snapshotContent, selection.anchorNode, selection.anchorOffset);
        const nativeAnchorOffset = nativeAnchor ? getTextOffset(snapshotContent, nativeAnchor) : undefined;

        mouseSelectionAnchor =
          nativeAnchor && nativeAnchorOffset !== 0 ? nativeAnchor : (mouseDownSelectionPoint ?? nativeAnchor);
      }

      if (isPointerWithinSnapshot && mousePointerCoordinates) {
        const nativeFocus = getTextSelectionPoint(snapshotContent, selection.focusNode, selection.focusOffset);
        const nativeFocusOffset = nativeFocus ? getTextOffset(snapshotContent, nativeFocus) : undefined;

        if (nativeFocus && nativeFocusOffset !== 0) {
          mouseSelectionFocus = nativeFocus;
        } else {
          mouseSelectionFocus =
            getTextSelectionPointAtCoordinates(snapshotContent, mousePointerCoordinates) ??
            mouseSelectionFocus ??
            mouseSelectionAnchor;
        }

        constrainedFocus = mouseSelectionFocus;
      } else {
        mouseSelectionFocus = undefined;
      }
    }

    constrainSelectionToElement(selection, snapshotContent, mouseSelectionAnchor, constrainedFocus);
  };

  const finishMouseSelection = (): void => {
    if (!isMouseSelecting) {
      return;
    }

    handleSelectionChange();
    resetMouseSelection();
  };

  const handleMouseDown = (event: MouseEvent): void => {
    const eventTarget = event.target;

    if (
      event.button !== 0 ||
      (eventTarget instanceof ownerWindow.Element && eventTarget.closest("[data-snapshot-row-action]"))
    ) {
      return;
    }

    isMouseSelecting = true;
    isCustomMouseSelecting = false;
    mousePointerCoordinates = { x: event.clientX, y: event.clientY };
    isPointerWithinSnapshot = isPointWithinElement(snapshotContent, mousePointerCoordinates);
    mouseDownSelectionPoint =
      event.shiftKey || event.detail > 1
        ? undefined
        : getTextSelectionPointAtCoordinates(snapshotContent, mousePointerCoordinates);
    mouseSelectionAnchor = undefined;
    mouseSelectionFocus = undefined;

    if (!mouseDownSelectionPoint) {
      return;
    }

    const selection = ownerDocument.getSelection();

    if (!selection) {
      return;
    }

    mouseSelectionAnchor = mouseDownSelectionPoint;
    mouseSelectionFocus = mouseDownSelectionPoint;

    try {
      selection.setBaseAndExtent(
        mouseDownSelectionPoint.node,
        mouseDownSelectionPoint.offset,
        mouseDownSelectionPoint.node,
        mouseDownSelectionPoint.offset,
      );
      isCustomMouseSelecting = true;
      event.preventDefault();
    } catch {
      mouseSelectionAnchor = undefined;
      mouseSelectionFocus = undefined;
    }
  };

  const handleMouseMove = (event: MouseEvent): void => {
    if (!isMouseSelecting) {
      return;
    }

    if ((event.buttons & 1) === 0) {
      finishMouseSelection();
      return;
    }

    mousePointerCoordinates = { x: event.clientX, y: event.clientY };
    isPointerWithinSnapshot = isPointWithinElement(snapshotContent, mousePointerCoordinates);

    if (!isCustomMouseSelecting || !mouseSelectionAnchor) {
      return;
    }

    event.preventDefault();

    const nextFocus = isPointerWithinSnapshot
      ? (getTextSelectionPointAtCoordinates(snapshotContent, mousePointerCoordinates) ?? mouseSelectionFocus)
      : getSelectionPointOutsideElement(snapshotContent, mousePointerCoordinates);
    const selection = ownerDocument.getSelection();

    if (selection && nextFocus) {
      mouseSelectionFocus = nextFocus;
      constrainSelectionToElement(selection, snapshotContent, mouseSelectionAnchor, mouseSelectionFocus);
    }
  };

  snapshotContent.addEventListener("mousedown", handleMouseDown);
  ownerDocument.addEventListener("mousemove", handleMouseMove);
  ownerDocument.addEventListener("selectionchange", handleSelectionChange);
  ownerDocument.addEventListener("mouseup", finishMouseSelection);
  ownerWindow.addEventListener("blur", finishMouseSelection);

  return () => {
    resetMouseSelection();
    snapshotContent.removeEventListener("mousedown", handleMouseDown);
    ownerDocument.removeEventListener("mousemove", handleMouseMove);
    ownerDocument.removeEventListener("selectionchange", handleSelectionChange);
    ownerDocument.removeEventListener("mouseup", finishMouseSelection);
    ownerWindow.removeEventListener("blur", finishMouseSelection);
  };
}
